import type { Chapter } from './types'

/**
 * Explore Mode structure: the complete, navigable proposal site.
 *
 * Presentation Mode does not follow these chapters. It runs its own recorded sequence, defined in
 * `presentation.ts`, which draws on scenes from several chapters in a different order.
 *
 * Primary navigation: Team | EAM | Challenge | Solution | Products | Architecture | Delivery | Investment.
 */
export const chapters: Chapter[] = [
  {
    id: 'opening',
    title: 'Opening',
    route: '/',
  },
  {
    id: 'team',
    navLabel: 'Team',
    part: 'Who is proposing',
    title: 'Merlion IT & Project Team',
    route: '/team',
    intro:
      'Merlion IT\'s corporate leadership, which sets the company\'s direction and supports every Merlion engagement, followed by the named professionals assigned to LAMS: their roles, alignment to the project, domain expertise, delivery record and practical Enterprise Asset Management experience.',
  },
  {
    id: 'eam',
    navLabel: 'EAM',
    part: 'The discipline',
    title: 'Enterprise Asset Management',
    route: '/eam',
    intro:
      'What Enterprise Asset Management is, why public land needs more than conventional EAM, and how LAMS applies the discipline to the District\'s Properties — from the concept of a strategic public property to a worked example of Land, Lake, Forest and River.',
  },
  {
    id: 'challenge',
    navLabel: 'Challenge',
    part: 'Project vision · Business',
    title: 'The Challenge',
    route: '/challenge',
    intro:
      'What the District asked for, the operational landscape it asked from, and how this proposal is organized around the evaluation priorities.',
  },
  {
    id: 'solution',
    navLabel: 'Solution',
    part: 'Project vision · Business',
    title: 'The LAMS Solution',
    route: '/solution',
    intro:
      'How LAMS organizes the complete land lifecycle around one durable Property context — acquisition, management and disposition, with GIS, work and documents cross-cutting.',
  },
  {
    id: 'products',
    navLabel: 'Products',
    part: 'Project vision · Technical',
    title: 'The LAMS Products',
    route: '/products',
    intro:
      'LAMS is delivered as three products that work together. LAMS Studio defines the application as governed metadata, LAMS Runtime interprets and enforces it, and LAMS Operations is the District-facing experience — with the essential modules of each and the project demand they answer.',
  },
  {
    id: 'architecture',
    navLabel: 'Architecture',
    part: 'Project vision · Technical',
    title: 'Technical Architecture',
    route: '/architecture',
    intro:
      'How LAMS is built: governed objects, an event layer, workflow, AI by design, security by design and native GIS, on a React, Node.js and SQL Server stack deployed in containers on District infrastructure — and how it integrates with CivicPlus, ArcGIS and the rest of the District landscape.',
  },
  {
    id: 'delivery',
    navLabel: 'Delivery',
    part: 'Project vision · Technical',
    title: 'Delivery & Support',
    route: '/delivery',
    intro:
      'Scrum as the delivery method, a 34-week program from January 1, 2027 with nine formal checkpoints, the migration, quality and risk controls behind it, the outcomes it is measured by, and twelve months of technical support after acceptance.',
  },
  {
    id: 'investment',
    navLabel: 'Investment',
    part: 'Project vision · Investment',
    title: 'Investment',
    route: '/investment',
    intro:
      'The not-to-exceed price for building the platform, how payment follows delivery checkpoints, the financial consequences we proposed against ourselves, and the separate investment for twelve months of technical support.',
  },
  {
    id: 'closing',
    title: 'Closing',
    route: '/closing',
  },
]

export const chapterById = new Map(chapters.map((c) => [c.id, c]))
